import React, { useMemo } from 'react';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import { TooltipComponent, GridComponent, LegendComponent, RadarComponent } from 'echarts/components';
import { BarChart, LineChart, RadarChart } from 'echarts/charts';
import { CanvasRenderer } from 'echarts/renderers';
import { Card, Col, Row } from 'react-bootstrap';
import { useAppContext } from '../../../providers/AppProvider';
import {
  LLM_CONTROL_KEYS,
  LLM_CONTROL_LABELS,
  ML_CONTROL_KEYS,
  ML_CONTROL_LABELS,
  type AISafetyRun,
  controlScore,
  filterControlEntriesByLane,
  filterRunsBySafetyLane,
  formatSafetyVersionTimestamp,
  readControls,
  resolveControlEntry,
  resolveInputType,
  runHasMlPrefixedControls
} from './aiSafety.utils';

echarts.use([
  TooltipComponent,
  GridComponent,
  LegendComponent,
  RadarComponent,
  BarChart,
  LineChart,
  RadarChart,
  CanvasRenderer
]);

type ControlBreakdownEntry = { key: string; label: string; score: number };

function humanizeControlKey(key: string): string {
  if (LLM_CONTROL_LABELS[key as keyof typeof LLM_CONTROL_LABELS]) {
    return LLM_CONTROL_LABELS[key as keyof typeof LLM_CONTROL_LABELS];
  }
  if (ML_CONTROL_LABELS[key as keyof typeof ML_CONTROL_LABELS]) {
    return ML_CONTROL_LABELS[key as keyof typeof ML_CONTROL_LABELS];
  }
  return key
    .replace(/^(llm_|ml_)/, '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}

function buildBreakdownFromControls(
  controls: Record<string, Record<string, unknown>>,
  lane?: 'ml' | 'llm'
): ControlBreakdownEntry[] {
  const filtered = lane ? filterControlEntriesByLane(controls, lane) : controls;
  return Object.entries(filtered)
    .map(([key, val]) => ({
      key,
      label: humanizeControlKey(key),
      score: controlScore(val) ?? 0
    }))
    .filter(e => e.score > 0 || Object.keys(filtered).length <= 8)
    .sort((a, b) => b.score - a.score);
}

function buildLlmBreakdown(run: AISafetyRun | null | undefined): ControlBreakdownEntry[] {
  const controls = readControls(run ?? undefined);
  return LLM_CONTROL_KEYS.map(key => ({
    key,
    label: LLM_CONTROL_LABELS[key],
    score: controlScore(resolveControlEntry(controls, key, 'llm')) ?? 0
  }));
}

function buildMlBreakdown(run: AISafetyRun | null | undefined): ControlBreakdownEntry[] {
  const controls = readControls(run ?? undefined);
  return ML_CONTROL_KEYS.map(key => ({
    key: `ml_${key}`,
    label: ML_CONTROL_LABELS[key],
    score: controlScore(controls[`ml_${key}`]) ?? 0
  }));
}

function buildBarOptions(
  controlBreakdown: ControlBreakdownEntry[],
  colors: {
    dangerColor: string;
    primaryColor: string;
    bodyHighlightBg: string;
    borderColor: string;
    lightTextEmphasis: string;
  }
) {
  const { dangerColor, primaryColor, bodyHighlightBg, borderColor, lightTextEmphasis } = colors;
  return {
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      backgroundColor: bodyHighlightBg,
      borderColor,
      textStyle: { color: lightTextEmphasis }
    },
    grid: { left: '3%', right: '8%', bottom: '3%', containLabel: true },
    xAxis: {
      type: 'value',
      max: 100,
      name: 'Contribution to Risk Score',
      axisLabel: { color: lightTextEmphasis }
    },
    yAxis: {
      type: 'category',
      data: controlBreakdown.map(c => c.label),
      axisLabel: { color: lightTextEmphasis, width: 120, overflow: 'truncate' }
    },
    series: [
      {
        type: 'bar',
        data: controlBreakdown.map(c => ({
          value: c.score,
          itemStyle: { color: c.score >= 70 ? dangerColor : primaryColor }
        })),
        label: { show: true, position: 'right', formatter: '{c}' }
      }
    ]
  };
}

function buildRiskScoreTrendOptions(
  runs: AISafetyRun[],
  colors: { primaryColor: string; bodyHighlightBg: string; borderColor: string; lightTextEmphasis: string },
  seriesName: string
) {
  const { primaryColor, bodyHighlightBg, borderColor, lightTextEmphasis } = colors;
  const sorted = [...runs].sort(
    (a, b) =>
      Date.parse(formatSafetyVersionTimestamp(a.timestamp) || '0') -
      Date.parse(formatSafetyVersionTimestamp(b.timestamp) || '0')
  );
  const labels = sorted.map(r => {
    const ts = formatSafetyVersionTimestamp(r.timestamp);
    if (!ts) return 'Run';
    try {
      return new Date(ts).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit' });
    } catch {
      return ts.slice(0, 16);
    }
  });
  const scores = sorted.map(r => Number(r.risk_score ?? r.Risk_Score ?? 0));

  return {
    tooltip: {
      trigger: 'axis',
      backgroundColor: bodyHighlightBg,
      borderColor,
      textStyle: { color: lightTextEmphasis }
    },
    grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
    xAxis: { type: 'category', data: labels, axisLabel: { color: lightTextEmphasis, rotate: 30 } },
    yAxis: { type: 'value', max: 100, name: 'Risk Score', axisLabel: { color: lightTextEmphasis } },
    series: [
      {
        type: 'line',
        name: seriesName,
        data: scores,
        smooth: true,
        itemStyle: { color: primaryColor },
        areaStyle: { opacity: 0.08 }
      }
    ]
  };
}

function buildMlRadarOptions(
  run: AISafetyRun | null | undefined,
  colors: { dangerColor: string; bodyHighlightBg: string; borderColor: string; lightTextEmphasis: string }
) {
  const { dangerColor, bodyHighlightBg, borderColor, lightTextEmphasis } = colors;
  const controls = readControls(run ?? undefined);
  const mlKeys = ML_CONTROL_KEYS.map(k => `ml_${k}`).filter(k => controls[k] != null);

  const keysToUse = mlKeys.length > 0 ? mlKeys : ML_CONTROL_KEYS.map(k => `ml_${k}`);
  const indicators = keysToUse.map(k => {
    const base = k.replace(/^ml_/, '') as (typeof ML_CONTROL_KEYS)[number];
    return { name: ML_CONTROL_LABELS[base] ?? humanizeControlKey(k), max: 100 };
  });
  const values = keysToUse.map(k => controlScore(controls[k]) ?? 0);

  return {
    tooltip: { backgroundColor: bodyHighlightBg, borderColor, textStyle: { color: lightTextEmphasis } },
    radar: {
      indicator: indicators.length ? indicators : [{ name: 'No ML controls', max: 100 }],
      axisName: { color: lightTextEmphasis }
    },
    series: [
      {
        type: 'radar',
        data: [{ value: values, name: 'ML Risk Surface', areaStyle: { opacity: 0.25 } }],
        itemStyle: { color: dangerColor }
      }
    ]
  };
}

interface AISafetyChartsProps {
  selectedRun: AISafetyRun | null | undefined;
  allRuns: AISafetyRun[];
  lane?: 'ml' | 'llm';
  /** Compliance IQ: LLM bar, ML bar, ML radar, and LLM risk score trend in split rows. */
  splitLaneCharts?: boolean;
  llmRun?: AISafetyRun | null | undefined;
  mlRun?: AISafetyRun | null | undefined;
}

function ChartCard({
  title,
  description,
  children
}: {
  title: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <Card className="h-100 border-0 shadow-sm">
      <Card.Body>
        <h6 className="mb-1">{title}</h6>
        <p className="small text-muted mb-3">{description}</p>
        {children}
      </Card.Body>
    </Card>
  );
}

export function AISafetyCharts({
  selectedRun,
  allRuns,
  lane,
  splitLaneCharts = false,
  llmRun,
  mlRun
}: AISafetyChartsProps) {
  const { getThemeColor } = useAppContext();
  const inputType = resolveInputType(selectedRun ?? undefined);
  const llmChartRun = llmRun ?? selectedRun;
  const mlChartRun = mlRun ?? selectedRun;

  const dangerColor = getThemeColor('danger') || '#dc3545';
  const primaryColor = getThemeColor('primary') || '#0d6efd';
  const bodyHighlightBg = getThemeColor('body-highlight-bg') || '#ffffff';
  const borderColor = getThemeColor('border-color') || '#dee2e6';
  const lightTextEmphasis = getThemeColor('light-text-emphasis') || '#495057';

  const chartColors = useMemo(
    () => ({ dangerColor, primaryColor, bodyHighlightBg, borderColor, lightTextEmphasis }),
    [dangerColor, primaryColor, bodyHighlightBg, borderColor, lightTextEmphasis]
  );

  const llmControlBreakdown = useMemo(() => buildLlmBreakdown(llmChartRun), [llmChartRun]);
  const mlControlBreakdown = useMemo(() => buildMlBreakdown(mlChartRun), [mlChartRun]);

  const controlBreakdown = useMemo(() => {
    const controls = readControls(selectedRun ?? undefined);
    return buildBreakdownFromControls(controls, lane);
  }, [selectedRun, lane]);

  const barOptions = useMemo(() => buildBarOptions(controlBreakdown, chartColors), [controlBreakdown, chartColors]);

  const llmBarOptions = useMemo(
    () => buildBarOptions(llmControlBreakdown, chartColors),
    [llmControlBreakdown, chartColors]
  );

  const mlBarOptions = useMemo(
    () => buildBarOptions(mlControlBreakdown, chartColors),
    [mlControlBreakdown, chartColors]
  );

  const radarOptions = useMemo(() => buildMlRadarOptions(mlChartRun, chartColors), [mlChartRun, chartColors]);

  const llmRuns = useMemo(() => filterRunsBySafetyLane(allRuns, 'llm'), [allRuns]);

  const llmRiskScoreTrendOptions = useMemo(
    () => buildRiskScoreTrendOptions(llmRuns, chartColors, 'LLM Risk Score'),
    [llmRuns, chartColors]
  );

  const trendOptions = useMemo(
    () => buildRiskScoreTrendOptions(allRuns, chartColors, 'Risk Score'),
    [allRuns, chartColors]
  );

  if (splitLaneCharts) {
    const barHeight = (count: number) => Math.max(280, count * 36);

    return (
      <>
        <Row className="g-3 mt-1">
          <Col xs={12} lg={6}>
            <ChartCard
              title="LLM Control Breakdown"
              description="LLM safety controls and their contribution to the overall risk score."
            >
              <ReactEChartsCore
                echarts={echarts}
                option={llmBarOptions}
                style={{ height: barHeight(llmControlBreakdown.length) }}
              />
            </ChartCard>
          </Col>
          <Col xs={12} lg={6}>
            <ChartCard
              title="LLM Risk Score Trend"
              description="LLM AI Safety risk score across evaluation versions (0–100 scale)."
            >
              {llmRuns.length > 0 ? (
                <ReactEChartsCore echarts={echarts} option={llmRiskScoreTrendOptions} style={{ height: 280 }} />
              ) : (
                <p className="small text-muted mb-0 py-5 text-center">No LLM evaluation runs available.</p>
              )}
            </ChartCard>
          </Col>
        </Row>
        <Row className="g-3 mt-1">
          <Col xs={12} lg={6}>
            <ChartCard
              title="ML Control Breakdown"
              description="ML safety controls and their contribution to the overall risk score."
            >
              <ReactEChartsCore
                echarts={echarts}
                option={mlBarOptions}
                style={{ height: barHeight(mlControlBreakdown.length) }}
              />
            </ChartCard>
          </Col>
          <Col xs={12} lg={6}>
            <ChartCard title="ML Risk Surface" description="ML model risk vectors across safety controls.">
              <ReactEChartsCore echarts={echarts} option={radarOptions} style={{ height: 320 }} />
            </ChartCard>
          </Col>
        </Row>
      </>
    );
  }

  const showRadar =
    lane === 'llm'
      ? false
      : runHasMlPrefixedControls(selectedRun ?? undefined) &&
        (lane === 'ml' || inputType === 'ML_APP' || inputType === 'BOTH');
  const hasControls = controlBreakdown.length > 0;
  const hasTrend = allRuns.length > 1;

  if (!hasControls && !hasTrend && !showRadar) {
    return null;
  }

  return (
    <Row className="g-3 mt-1">
      {hasControls ? (
        <Col xs={12} lg={showRadar ? 7 : 12}>
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body>
              <h6 className="mb-1">Control Breakdown</h6>
              <p className="small text-muted mb-3">Contribution of each safety control to the overall risk score.</p>
              <ReactEChartsCore
                echarts={echarts}
                option={barOptions}
                style={{ height: Math.max(280, controlBreakdown.length * 36) }}
              />
            </Card.Body>
          </Card>
        </Col>
      ) : null}
      {showRadar ? (
        <Col xs={12} lg={hasControls ? 5 : 6}>
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body>
              <h6 className="mb-1">ML Risk Surface</h6>
              <p className="small text-muted mb-3">Model risk vectors across ML safety controls.</p>
              <ReactEChartsCore echarts={echarts} option={radarOptions} style={{ height: 320 }} />
            </Card.Body>
          </Card>
        </Col>
      ) : null}
      {hasTrend ? (
        <Col xs={12}>
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <h6 className="mb-1">Risk Score Trend</h6>
              <p className="small text-muted mb-3">AI Safety risk score across evaluation versions.</p>
              <ReactEChartsCore echarts={echarts} option={trendOptions} style={{ height: 280 }} />
            </Card.Body>
          </Card>
        </Col>
      ) : null}
    </Row>
  );
}

export default AISafetyCharts;
